/**
 * "Compare against: (•) Design (◦) Earlier survey" (spec §12.1). The mode
 * only renames the two roles and decides what Fill / Cut mean on screen; the
 * engines never see it beyond the stored `compareKind`.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { COMPARE_KINDS, type CompareKind } from '@/lib/analyses/compare/availability';

const KIND_LABEL_KEYS = {
  design: 'compareSurfaces.kind.design',
  survey: 'compareSurfaces.kind.survey',
} as const satisfies Record<CompareKind, string>;

export interface CompareKindToggleProps {
  value: CompareKind;
  onChange: (kind: CompareKind) => void;
}

export function CompareKindToggle({ value, onChange }: CompareKindToggleProps) {
  const { t } = useTranslation('project');
  const id = useId();
  return (
    <div data-testid='compare-kind' className='flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2'>
      <span id={`${id}-label`} className='text-sm font-medium'>
        {t('compareSurfaces.kind.label')}
      </span>
      <RadioGroup
        aria-labelledby={`${id}-label`}
        orientation='horizontal'
        value={value}
        onValueChange={(v) => onChange(v as CompareKind)}
        className='flex flex-wrap items-center gap-4'>
        {COMPARE_KINDS.map((k) => (
          <div key={k} className='flex items-center gap-2'>
            <RadioGroupItem id={`${id}-${k}`} value={k} />
            <Label htmlFor={`${id}-${k}`} className='font-normal'>
              {t(KIND_LABEL_KEYS[k])}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}
